import React, { useEffect, useRef } from 'react';

/**
 * Full-bleed hero photograph with a scroll-driven parallax, in the manner of
 * Apple's product pages: the image sits behind the content, drifts slower than
 * the page, and eases out of its initial overscale as you scroll.
 *
 * Implementation notes:
 *
 * - The image is a CSS `background-image`, not an `<img>`. If the file is
 *   missing the layer simply paints nothing and the aurora shows through, so a
 *   missing asset degrades instead of leaving a broken-image box.
 * - It starts at `scale(1.12)` so there is room to translate without exposing
 *   an edge. Parallax that reveals the boundary of its own image is worse than
 *   no parallax.
 * - Only `transform` is animated, driven from a rAF-throttled scroll listener.
 *   Touching `background-position` here would relayout on every frame.
 */
export default function HeroMedia({
  alt = 'A therapist and client talking in a bright, comfortable room',
}) {
  const layerRef = useRef(null);

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;

    // Anyone who has asked the OS to reduce motion gets a still image.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (reduced?.matches) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      // Past the hero's own height there is nothing left to parallax.
      const progress = Math.min(y / 700, 1);
      const shift = y * 0.32;
      const scale = 1.12 - progress * 0.07;
      el.style.transform = `translate3d(0, ${shift}px, 0) scale(${scale})`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="mm-hero__media" aria-hidden="true">
      {/* The image itself is declared in SCSS via image-set, so WebP is served
          where supported and JPEG everywhere else. Setting it inline here
          would override that and force the heavier file on every browser. */}
      <div
        className="mm-hero__media-layer"
        ref={layerRef}
        role="img"
        aria-label={alt}
      />
      {/* Separate element so the scrim never inherits the parallax transform —
          it has to stay locked to the text it is protecting. */}
      <div className="mm-hero__scrim" />
    </div>
  );
}
